import { useEffect, useRef, useState } from "react";
import { UseEntityStore } from "../../common/entityStore";
import { createSignalRConnection } from "../../common/signalr";
import { PageTitle } from "../../layout/PageTitle";
import { createListStore } from "../../common/listStore";
import { BehaviorSubject, map } from "rxjs";
import { RunLogEntry } from "./common";
import { downloadFromApi } from "../../common/download";
import { useCommStore } from "../../common/axios";
import { Alert, Button, Grid, Typography } from "@mui/material";
import { DirectionsRun } from "@mui/icons-material";
import { renderListStore } from "../../common/listStoreUi";
import { useLogEntryTypes } from "../../common/enum";
import dayjs from "dayjs";
import { delay } from "../../common/utils";
import { formatDateTime } from "../../common/localization";
import { useDefaultValues } from "../../common/defaultValues";
import { useAccountStore } from "../../common/account";
import { groupBy, orderBy, toArray } from "lodash";

export function Run<TParams>({ name, fake, hub, useParamsStore, renderParams, processResult, checkData, onRunFinished, setIsRunning, paramsWidth }: {
    name: string;
    fake?: boolean;
    hub: string;
    useParamsStore: UseEntityStore<TParams>;
    renderParams?(): JSX.Element;
    processResult?(result: any, runAgain: () => void): void;
    checkData?(callSignalrMethod: (name: string) => Promise<{ success: boolean; result?: any }>): Promise<boolean>;
    onRunFinished?(success: boolean): void;
    setIsRunning?(isRunning: boolean): void;
    paramsWidth?: number;
}) {
    const [running, setRunning] = useState(false);
    const { landCode } = useDefaultValues(p => ({ landCode: p.clientData.landCode }));
    const [logEntries$] = useState(() => new BehaviorSubject<RunLogEntry[]>([]));
    const [useLogStore] = useState(() => createListStore({
        data: logEntries$.pipe(map(p => ({ items: p, isFetching: false }))),
        itemsPerPage: 15,
        listProps: [
            { column: "id", label: "#", width: 70, type: "number" },
            { column: "time", label: "Time", width: 150, type: "datetime" },
            { column: "type", label: "Type", width: 100, enumStore: useLogEntryTypes },
            { column: "message", label: "Message", flex: 1 },
        ],
    }));

    const { arePropsInvalid } = useParamsStore(p => ({ arePropsInvalid: p.isSaveDisabled }));
    const customId = useRef<string | null>();
    const [error, setError] = useState<string | null>(null);

    async function run() {
        useParamsStore.getState().enableValidation();
        if (useParamsStore.getState().isSaveDisabled) {
            return;
        }
        let canRun = true;
        if (customId.current == null && checkData) {
            canRun = await checkData(callSignalrMethod);
        }
        if (canRun) {
            const { success } = await callSignalrMethod("run");
            if (success) {
                customId.current = null;
            }
            onRunFinished && onRunFinished(success);
        }
    };

    const runDisconnect = useRef<() => void>();
    useEffect(() => {
        return () => {
            runDisconnect.current?.();
        };
    }, []);
    
    function replaceEntries(entries: RunLogEntry[]) {
        const finalEntries = orderBy(toArray(groupBy(logEntries$.value.concat(entries), p => p.id)).map(p => p.at(-1)!), p => p.id);
        logEntries$.next(finalEntries);
    }

    async function callSignalrMethod(method: string) {
        setRunning(true);
        setIsRunning?.(true);
        logEntries$.next([]);
        let isRequesting = false;
        while (true) {
            let isStopped = false;
            await useAccountStore.getState().refreshAccessToken(true);
            const connection = createSignalRConnection(hub);
            runDisconnect.current = () => {
                isStopped = true;
                connection.stop();
            };
            connection.on("log", (entry: RunLogEntry) => {
                replaceEntries([entry]);
            });
            connection.on("logs", (entries: RunLogEntry[]) => {
                replaceEntries(entries);
            });
            connection.on("customId", (id: string) => {
                customId.current = id;
                return true;
            });
            try {
                if (!isRequesting) {
                    useCommStore.getState().requestStarted();
                    isRequesting = true;
                }
                await connection.start();
                setError(null);
                const data = {
                    ...useParamsStore.getState().clientData,
                    isFake: fake,
                    customId: customId.current,
                };
                const result = await connection.invoke(method, data);
                await connection.stop();
                if (result.zipFileName || result.fileName) {
                    downloadFromApi(`runs/runs/${result.runId}/resultFile`);
                }
                setRunning(false);
                setIsRunning?.(false);
                useCommStore.getState().requestFinished(undefined);
                processResult && processResult(result, run);
                return { success: true, result };
            } catch (e) {
                const shouldReconnect = customId.current != null && !isStopped;
                setError(`Error: ${e}`);
                if (shouldReconnect) {
                    setError(`${formatDateTime(dayjs().toISOString(), landCode)} Reconnecting in 5 seconds: ${e}`);
                    await delay(5000);
                } else {
                    setRunning(false);
                    setIsRunning?.(false);
                    if (isStopped) {
                        useCommStore.getState().requestFinished(undefined);
                    } else {
                        setError(`${formatDateTime(dayjs().toISOString(), landCode)} Error: ${e}`);
                        useCommStore.getState().requestFinished(e);
                    }
                    return { success: false };
                }
            } finally {
                runDisconnect.current = undefined;
                await connection.stop();
            }
        }
    }

    return <>
        <PageTitle title={`${name}${fake ? " *FAKE*" : ""}`} />
        <Grid container alignItems="flex-start" columnSpacing={4}>
            <Grid item container width={paramsWidth ?? 450}>
            {renderParams && <Grid item><Typography variant="h6" mb={1}>Parameters</Typography></Grid>}
                {renderParams && <Grid item container rowSpacing={2}>{renderParams()}</Grid>}
                <Grid item mt={2}><Button type="submit" onClick={run} disabled={running || arePropsInvalid} size="large"><DirectionsRun /> Run</Button></Grid>
                {error && <Grid item mt={2}><Alert severity="error">{error}</Alert></Grid>}
            </Grid>
            <Grid item xs>
                <Typography variant="h6" mb={2.6}>Run log</Typography>
                {renderListStore(useLogStore)}
            </Grid>
        </Grid>
    </>;
}
